import { browserFetch } from '../../services/browserSession';
import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users,
  ChevronLeft,
  Check,
  X,
  Loader2,
  Sparkles,
  ExternalLink,
  Instagram,
  Search,
  Clock3,
  CircleCheckBig,
  CircleX,
} from 'lucide-react';
import Button from '../common/design-system/Button';
import { Badge } from '../common/design-system/Badge';
import Skeleton from '../common/design-system/Skeleton';
import styles from './BusinessApplications.module.scss';
import clsx from 'clsx';
import Avatar from '../common/Avatar';

const STATUS_FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'pending', label: 'Pendientes' },
  { id: 'selected', label: 'Seleccionados' },
  { id: 'rejected', label: 'Rechazados' },
];

const PAGE_POLL_INTERVAL_MS = 30000;

const getNormalizedStatus = (status) => {
  if (status === 'accepted' || status === 'approved') return 'selected';
  if (status === 'rejected') return 'rejected';
  return 'pending';
};

const hasSubmittedDeliverables = (application) => (
  Array.isArray(application.deliverables) && application.deliverables.length > 0
);

const getReviewStatus = (application) => {
  const normalizedStatus = getNormalizedStatus(application.status);
  if (normalizedStatus === 'pending' && hasSubmittedDeliverables(application)) {
    return 'selected';
  }
  return normalizedStatus;
};

const readApiError = async (response, fallback) => {
  try {
    const data = await response.json();
    return data?.error || data?.detail || fallback;
  } catch (error) {
    return fallback;
  }
};

const isCapacityError = (message) => (
  String(message || '').toLowerCase().includes('numero de creadores aceptados')
);

const OfferApplications = () => {
  const { t } = useTranslation(['business', 'collab', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchApplications = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {

      const [offerRes, appsRes] = await Promise.all([
        browserFetch(`/api/collabs/collab/${id}/summary/`),
        browserFetch(`/api/feed/applications/?offer=${id}`),
      ]);

      if (offerRes.ok) setOfferDetails(await offerRes.json());
      if (appsRes.ok) {
        const data = await appsRes.json();
        setApplications(Array.isArray(data.results) ? data.results : data);
      } else {
        throw new Error('Error al cargar aplicaciones.');
      }
    } catch (err) {
      console.error('Fetch error:', err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) fetchApplications({ silent: true });
    };
    const interval = window.setInterval(refresh, PAGE_POLL_INTERVAL_MS);
    const handleVisibleRefresh = () => {
      if (!document.hidden) refresh();
    };

    window.addEventListener('focus', refresh);
    window.addEventListener('foth:applications-refresh', refresh);
    document.addEventListener('visibilitychange', handleVisibleRefresh);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('foth:applications-refresh', refresh);
      document.removeEventListener('visibilitychange', handleVisibleRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const approveApplicationRequest = async (applicationId, { increaseCapacity = false } = {}) => {

    const res = await browserFetch(`/api/feed/applications/${applicationId}/approve/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ increase_capacity: increaseCapacity }),
    });

    if (!res.ok) throw new Error(await readApiError(res, 'Error al aprobar'));
    return res.json();
  };

  const handleApproveApplication = async (applicationId) => {
    setProcessingAction(applicationId);
    try {
      await approveApplicationRequest(applicationId);
      setApplications((prev) => prev.map((app) => (
        app.id === applicationId ? { ...app, status: 'accepted' } : app
      )));
      setOfferDetails((prev) => (
        prev ? { ...prev, quantity: Math.max(Number(prev.quantity || 0), stats.selected + 1) } : prev
      ));
      window.dispatchEvent(new Event('foth:notifications-refresh'));
    } catch (err) {
      if (isCapacityError(err.message)) {
        const shouldIncreaseCapacity = window.confirm(
          'Esta collab ya alcanzo su cupo actual de creadores aceptados. Quieres aumentar el cupo en 1 y aprobar este candidato?'
        );

        if (shouldIncreaseCapacity) {
          try {
            await approveApplicationRequest(applicationId, { increaseCapacity: true });
            setApplications((prev) => prev.map((app) => (
              app.id === applicationId ? { ...app, status: 'accepted' } : app
            )));
            setOfferDetails((prev) => (
              prev ? { ...prev, quantity: Math.max(Number(prev.quantity || 0) + 1, stats.selected + 1) } : prev
            ));
            window.dispatchEvent(new Event('foth:notifications-refresh'));
            return;
          } catch (retryError) {
            alert(retryError.message);
            return;
          }
        }
      } else {
        alert(err.message);
      }
    } finally {
      setProcessingAction(null);
    }
  };

  const handleRejectApplication = async (applicationId) => {
    if (!window.confirm(t('applications_pipeline.reject_confirm', '¿Seguro que deseas rechazar esta aplicación?'))) return;
    setProcessingAction(applicationId);
    try {

      const res = await browserFetch(`/api/feed/applications/${applicationId}/reject/`, {
        method: 'POST',
        });
      if (!res.ok) throw new Error(await readApiError(res, 'Error al rechazar'));
      setApplications((prev) => prev.map((app) => (
        app.id === applicationId ? { ...app, status: 'rejected' } : app
      )));
      window.dispatchEvent(new Event('foth:notifications-refresh'));
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const getCreatorName = (app) => {
    if (app.creator_profile?.full_name) return app.creator_profile.full_name;
    return app.creator_username || app.user?.username || 'Talento FOTH';
  };

  const getCreatorUsername = (app) => app.creator_username || app.user?.username || '';

  const stats = useMemo(() => ({
    total: applications.length,
    pending: applications.filter((app) => getReviewStatus(app) === 'pending').length,
    selected: applications.filter((app) => getReviewStatus(app) === 'selected').length,
    rejected: applications.filter((app) => getReviewStatus(app) === 'rejected').length,
  }), [applications]);

  const acceptedCapacity = Number(offerDetails?.quantity || 0);

  const filteredApplications = useMemo(() => applications.filter((app) => {
    const normalizedStatus = getReviewStatus(app);
    const creatorName = getCreatorName(app).toLowerCase();
    const username = getCreatorUsername(app).toLowerCase();
    const matchesFilter = activeFilter === 'all' || normalizedStatus === activeFilter;
    const matchesSearch = `${creatorName} ${username}`.includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  }), [applications, activeFilter, searchTerm]);

  if (loading) {
    return (
      <div className={styles['applicants-page']}>
        <div className={styles.container}>
          <div className={styles['loading-stack']}>
            <Skeleton height="220px" borderRadius="32px" />
            <Skeleton height="140px" borderRadius="24px" />
            <Skeleton height="420px" borderRadius="24px" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['applicants-page']}>
      <div className={styles.container}>
        <section className={styles['hero-section']}>
          <div className={styles['hero-inner']}>
            <div className={styles['hero-topline']}>
              <div className={styles['badge-pipeline']}>
                <Users size={14} /> {t('applications_pipeline.title', 'Applicants Pipeline')}
              </div>
              <button
                className={styles['back-button']}
                onClick={() => navigate(`/business/collabs/${id}/summary`)}
              >
                <ChevronLeft size={16} /> {t('applications_pipeline.back', 'Volver al Collab')}
              </button>
            </div>

              <div className={styles['hero-meta']}>
                <Badge variant="success">{stats.total} Postulaciones</Badge>
                {acceptedCapacity > 0 && (
                  <Badge variant={stats.selected >= acceptedCapacity ? 'warning' : 'invite'}>
                    {stats.selected}/{acceptedCapacity} cupos aceptados
                  </Badge>
                )}
                <span>{t('applications_pipeline.managing_title', 'Gestionando candidatos')}</span>
              </div>

            <h1>{offerDetails?.title}</h1>
            <p>{t('applications_pipeline.managing_desc', 'Evalúa perfiles, decide rápido y sigue el progreso de cada creador desde un solo lugar.')}</p>

            <div className={styles['stats-grid']}>
              <div className={styles['stat-card']}>
                <div className={styles['stat-icon']}><Users size={18} /></div>
                <div className={styles['stat-info']}>
                  <div className={styles.label}>Total</div>
                  <div className={styles.value}>{stats.total}</div>
                  <div className={styles.helper}>Postulaciones recibidas</div>
                </div>
              </div>
              <div className={styles['stat-card']}>
                <div className={`${styles['stat-icon']} ${styles.warning}`}><Clock3 size={18} /></div>
                <div className={styles['stat-info']}>
                  <div className={styles.label}>Pendientes</div>
                  <div className={styles.value}>{stats.pending}</div>
                  <div className={styles.helper}>Requieren decisión</div>
                </div>
              </div>
              <div className={styles['stat-card']}>
                <div className={`${styles['stat-icon']} ${styles.accent}`}><CircleCheckBig size={18} /></div>
                <div className={styles['stat-info']}>
                  <div className={styles.label}>Seleccionados</div>
                  <div className={styles.value}>{stats.selected}</div>
                  <div className={styles.helper}>Listos para avanzar</div>
                </div>
              </div>
              <div className={styles['stat-card']}>
                <div className={`${styles['stat-icon']} ${styles.danger}`}><CircleX size={18} /></div>
                <div className={styles['stat-info']}>
                  <div className={styles.label}>Rechazados</div>
                  <div className={styles.value}>{stats.rejected}</div>
                  <div className={styles.helper}>Fuera del proceso</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={styles['review-toolbar']}>
          <div>
            <div className={styles['section-kicker']}>
              <Sparkles size={14} />
              Candidate Review
            </div>
            <h2>Candidatos Talentosos</h2>
          </div>

          <div className={styles['toolbar-controls']}>
            <div className={styles['search-wrapper']}>
              <Search size={16} />
              <input
                type="text"
                value={searchTerm}
                placeholder={t("common:search", "Buscar creador...")}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className={styles['filter-pills']}>
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  className={activeFilter === filter.id ? styles.active : ''}
                  onClick={() => setActiveFilter(filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className={styles['group-card']}>
          <div className={styles['applications-table-header']}>
            <div>Creador</div>
            <div>Fecha de aplicación</div>
            <div>Estado actual</div>
            <div>Acciones</div>
          </div>

          <div className={styles['applications-list']}>
            {filteredApplications.map((app) => {
              const normalizedStatus = getReviewStatus(app);
              return (
                <div key={app.id} className={styles['applicant-row']}>
                  <div className={styles['creator-info']}>
                    <div className={styles['avatar-box']}>
                      <Avatar
                        size="md"
                        src={app.creator_profile?.profile_picture_url || app.creator_profile?.avatar_url || app.creator_profile?.user?.profile_picture_url}
                        fallbackSrc={app.creator_profile?.ig_profile_picture_url}
                        name={getCreatorName(app)}
                        alt={getCreatorName(app)}
                      />
                    </div>
                    <div className={styles['creator-copy']}>
                      <span className={styles.name}>{getCreatorName(app)}</span>
                      <span>@{getCreatorUsername(app) || 'creator'}</span>
                    </div>
                  </div>

                  <div className={styles.date}>
                    {new Date(app.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                  </div>

                  <div className={styles['status-stack']}>
                    <span className={clsx(
                      styles['status-badge'],
                      styles[`status-${normalizedStatus}`]
                    )}>
                      {normalizedStatus === 'selected' ? 'Seleccionado' : normalizedStatus === 'rejected' ? 'Rechazado' : 'Pendiente'}
                    </span>
                    {hasSubmittedDeliverables(app) && (
                      <span className={styles['deliverable-note']}>
                        <Instagram size={12} /> {app.deliverables.length} entregable{app.deliverables.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className={styles.actions}>
                    {normalizedStatus === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className={styles['approve-button']}
                          onClick={() => handleApproveApplication(app.id)}
                          disabled={processingAction === app.id}
                        >
                          {processingAction === app.id ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className={styles['reject-button']}
                          onClick={() => handleRejectApplication(app.id)}
                          disabled={processingAction === app.id}
                        >
                          <X size={16} />
                          Rechazar
                        </Button>
                      </>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/profile/${getCreatorUsername(app)}`)}
                      disabled={!getCreatorUsername(app)}
                      title="Ver perfil"
                    >
                      <ExternalLink size={16} />
                      Perfil
                    </Button>

                    {app.deliverables?.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={styles['metrics-button']}
                        onClick={() => navigate(`/business/collabs/${id}/journey?step=deliverables${app.creator_username ? `&creator=${encodeURIComponent(app.creator_username)}` : ''}`)}
                        title="Revisar entrega"
                      >
                        <Instagram size={16} />
                        Revisar entrega
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}

            {filteredApplications.length === 0 && (
              <div className={styles['empty-state-card']}>
                <Users className={styles.icon} size={48} />
                <h3>{applications.length === 0 ? 'Sin candidatos' : 'No encontramos coincidencias'}</h3>
                <p>
                  {applications.length === 0
                    ? 'Aún no hay candidatos postulados para esta campaña específica.'
                    : 'Prueba con otro filtro o ajusta tu búsqueda para encontrar al creador correcto.'}
                </p>
                {applications.length === 0 && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/business/collabs/${id}/summary`)}>
                    Ver kit viral para atraer creadores
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferApplications;
